/**
 * StoricoVariazioniDialog — analisi del costo di una ricetta o piatto menu:
 *  - riepilogo costo attuale vs salvato (+ margine per i menu)
 *  - COMPOSIZIONE costo: quanto pesa ogni ingrediente/componente + ultima variazione
 *  - GRAFICO andamento nel tempo
 *  - STORICO variazioni con il PERCHÉ (quale ingrediente è cambiato, da→a) e QUANDO
 * Dati ricostruiti dallo storico prezzi datato degli ingredienti/materiali.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ArrowUpRight, ArrowDownRight, TrendingUp, Loader2, ArrowRight } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = sessionStorage.getItem("token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const shortDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return isNaN(d) ? String(s).slice(0, 10) : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" });
};

const DeltaBadge = ({ delta, invert = false }) => {
  if (delta == null) return null;
  const up = delta > 0;
  const good = invert ? up : !up;
  const cls = good
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-orange-50 text-orange-700 border-orange-200";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${cls}`} data-testid="metric-variation-badge">
      <Icon className="mr-0.5 h-3 w-3" />{up ? "+" : ""}{Number(delta).toFixed(1)}%
    </span>
  );
};

const tipoLabel = { ingrediente: "Ingrediente", ricetta: "Ricetta", packaging: "Packaging" };

export default function StoricoVariazioniDialog({ open, onOpenChange, itemId, itemNome, tipo = "ricetta", prezzoVendita = 0, localeId = null }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const isMenu = tipo === "menu";
  const dec = isMenu ? 2 : 2;
  const eur = (n) => (n == null ? "—" : `€${Number(n).toFixed(dec)}`);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ item_id: itemId, item_type: tipo });
      if (localeId) params.set("locale_id", localeId);
      const res = await axios.get(`${API}/costi/analisi?${params.toString()}`, { headers: authHeaders() });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [itemId, tipo, localeId]);

  useEffect(() => { if (open) { setData(null); load(); } }, [open, load]);

  const unit = data?.unita || (isMenu ? "€" : "€/kg");
  const valueLabel = data?.valore_label || (isMenu ? "Food cost" : "Costo");
  const costoAttuale = data?.costo_attuale ?? null;
  const costoSalvato = data?.costo_salvato ?? null;
  const deltaSalvato = (costoSalvato && costoAttuale != null) ? ((costoAttuale - costoSalvato) / costoSalvato) * 100 : null;
  const pv = data?.prezzo_vendita || prezzoVendita || 0;
  const margineAttuale = (isMenu && pv > 0 && costoAttuale != null) ? ((pv - costoAttuale) / pv) * 100 : null;

  const componenti = data?.componenti || [];
  const timeline = data?.timeline || [];
  const chartData = timeline.map((t) => ({ label: shortDate(t.data), valore: t.valore }));
  const changes = [...timeline].filter((t) => t.delta_pct != null && (Math.abs(t.delta_pct) > 0 || (t.cause && t.cause.length))).reverse();
  const driver = componenti[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="storico-variazioni-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <TrendingUp className="h-4 w-4 text-amber-500" />
            Analisi costo — {itemNome}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : !data ? (
          <div className="py-10 text-center text-sm text-slate-500">Impossibile caricare l'analisi.</div>
        ) : (
          <>
            {/* Riepilogo */}
            <div className="flex flex-wrap items-center gap-4 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="analisi-riepilogo">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">{valueLabel} attuale</p>
                <p className="font-mono font-bold text-lg text-slate-900 flex items-center gap-2" data-testid="storico-valore-attuale">
                  {eur(costoAttuale)}<span className="text-xs text-slate-400">{unit}</span>
                  <DeltaBadge delta={deltaSalvato} />
                </p>
                {costoSalvato != null && deltaSalvato != null && Math.abs(deltaSalvato) >= 0.1 && (
                  <p className="text-[11px] text-slate-400">era {eur(costoSalvato)} {unit}</p>
                )}
              </div>
              {isMenu && margineAttuale != null && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Margine attuale</p>
                  <p className="font-mono font-bold text-lg text-emerald-600">{margineAttuale.toFixed(1)}%</p>
                </div>
              )}
            </div>

            {/* Causa principale */}
            {driver?.ultima_variazione && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-testid="analisi-causa-principale">
                <TrendingUp className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-snug">
                  Variazione trainata da <b>{driver.nome}</b>: {eur(driver.ultima_variazione.da)}{" "}
                  <ArrowRight className="inline h-3 w-3 -mt-0.5" /> {eur(driver.ultima_variazione.a)} {driver.um}{" "}
                  <span className="font-semibold">({driver.ultima_variazione.delta_pct > 0 ? "+" : ""}{driver.ultima_variazione.delta_pct}%)</span>{" "}
                  il {shortDate(driver.ultima_variazione.data)}.
                </p>
              </div>
            )}

            {/* Composizione costo */}
            {componenti.length > 0 && (
              <div className="mt-3" data-testid="analisi-composizione">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Composizione costo</p>
                <div className="space-y-1.5">
                  {componenti.map((c, i) => (
                    <div key={i} className="rounded-lg bg-white border border-slate-100 px-3 py-2" data-testid="analisi-componente">
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-slate-800 truncate">{c.nome}</span>
                          <span className="text-[9px] uppercase tracking-wide text-slate-400 border border-slate-200 rounded px-1 py-0.5 shrink-0">{tipoLabel[c.tipo] || c.tipo}</span>
                        </span>
                        <span className="text-xs font-semibold text-slate-700 shrink-0">{c.contributo_pct}% · {eur(c.prezzo_attuale)} {c.um}</span>
                      </div>
                      {/* barra contributo */}
                      <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-amber-400" style={{ width: `${Math.min(100, c.contributo_pct)}%` }} />
                      </div>
                      {c.ultima_variazione && (
                        <p className="mt-1 text-[11px] text-slate-500 flex items-center gap-1">
                          {eur(c.ultima_variazione.da)} <ArrowRight className="h-3 w-3" /> {eur(c.ultima_variazione.a)} {c.um}
                          <DeltaBadge delta={c.ultima_variazione.delta_pct} />
                          <span className="text-slate-400">· {shortDate(c.ultima_variazione.data)}</span>
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Grafico andamento */}
            {chartData.length > 1 && (
              <div className="mt-3">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Andamento {valueLabel.toLowerCase()}</p>
                <div className="h-40 w-full animate-in fade-in duration-500" data-testid="history-chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradVal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 10, fill: "#64748B" }} width={44} domain={["auto", "auto"]} />
                      <Tooltip formatter={(v) => [`€${Number(v).toFixed(dec)}`, valueLabel]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Area type="monotone" dataKey="valore" stroke="#F59E0B" strokeWidth={2} fill="url(#gradVal)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Storico variazioni con causa */}
            <div className="mt-3" data-testid="analisi-storico">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Storico variazioni</p>
              {changes.length === 0 ? (
                <p className="py-4 text-center text-xs text-slate-400">
                  Nessuna variazione registrata nello storico prezzi.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {changes.map((ev, i) => {
                    const prevVal = ev.valore / (1 + (ev.delta_pct || 0) / 100);
                    return (
                      <div key={i} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2" data-testid="history-list-item">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-slate-500">{shortDate(ev.data)}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-slate-400 line-through">{eur(prevVal)}</span>
                            <span className="font-mono text-sm font-semibold text-slate-800">{eur(ev.valore)}</span>
                            <DeltaBadge delta={ev.delta_pct} />
                          </div>
                        </div>
                        {ev.cause && ev.cause.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {ev.cause.map((cs, j) => (
                              <span key={j} className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-200 rounded-full px-2 py-0.5 text-slate-600" data-testid="history-cause-chip">
                                <b className="text-slate-800">{cs.nome}</b>
                                {eur(cs.da)}<ArrowRight className="h-3 w-3" />{eur(cs.a)}
                                <span className={cs.delta_pct > 0 ? "text-orange-600 font-semibold" : "text-emerald-600 font-semibold"}>
                                  {cs.delta_pct > 0 ? "+" : ""}{cs.delta_pct}%
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { DeltaBadge };
